import React, { useState, useMemo, useEffect } from "react";

// mWHO risk levels in order of severity (index = severity score)
const WHO_LEVELS = ["I", "II", "II-III", "III", "IV"];

const getLevelIndex = (level) => WHO_LEVELS.indexOf(level);

// Convert mWHO level to URL parameter format
const levelToUrlParam = (level) => {
  const mapping = {
    I: "i",
    II: "ii",
    "II-III": "ii-iii",
    III: "iii",
    IV: "iv",
  };
  return mapping[level] || null;
};

// Convert URL parameter back to mWHO level
const urlParamToLevel = (param) => {
  const mapping = {
    i: "I",
    ii: "II",
    "ii-iii": "II-III",
    iii: "III",
    iv: "IV",
  };
  return mapping[param] || null;
};

// Get URL query parameters
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return {
    results: urlParams.get("results"),
  };
};

// Survey question data structure
const questions = [
  {
    id: "q1",
    text: "Do you have heart failure?",
    subQuestions: [
      {
        id: "q1a",
        text: "Heart failure classification:",
        footnote: 'LVEF: Left ventricular ejection fraction',
        options: [
          {
            id: "q1a1",
            text: "Mildly reduced left ventricular systolic function (LVEF >45%)",
            level: "II-III",
          },
          {
            id: "q1a2",
            text: "Moderately reduced left ventricular systolic function (LVEF 30-45%)",
            level: "III",
          },
          {
            id: "q1a3",
            text: "Severely reduced left ventricular systolic function (LVEF <30%)",
            level: "IV",
          },
          {
            id: "q1a4",
            text: "Significantly impaired right ventricular function",
            level: "II-III",
          },
        ],
      },
    ],
  },
  {
    id: "q2",
    text: "Do you have peripartum cardiomyopathy?",
    subQuestions: [
      {
        id: "q2a",
        text: "Peripartum cardiomyopathy status:",
        footnote: 'LVEF: Left ventricular ejection fraction',
        options: [
          {
            id: "q2a1",
            text: "Previous peripartum cardiomyopathy with current LVEF >50%",
            level: "III",
          },
          {
            id: "q2a2",
            text: "Previous peripartum cardiomyopathy with current LVEF <50%",
            level: "IV",
          },
        ],
      },
    ],
  },
  {
    id: "q3",
    text: "Do you have abnormal heart beats (arrhythmias) not related to an inherited syndrome that can cause abnormal heart beats (arrhythmias)?",
    subQuestions: [
      {
        id: "q3a",
        text: "Type of arrhythmia:",
        options: [
          {
            id: "q3a1",
            text: "Atrial or ventricular ectopic beats (PACs, or PVCs)",
            level: "I",
          },
          {
            id: "q3a2",
            text: "Supraventricular arrhythmias (e.g. AVNRT, AVRT, atrial tachycardia)",
            level: "II",
          },
          { id: "q3a3", text: "Bradycardia requiring pacemaker", level: "II" },
          {
            id: "q3a4",
            text: "Sustained ventricular tachycardia",
            level: "III",
          },
        ],
      },
    ],
  },
  {
    id: "q4",
    text: "Do you have an inherited syndrome that can cause abnormal heart beats (arrhythmias)?",
    subQuestions: [
      {
        id: "q4a",
        text: "Inherited arrhythmia syndrome:",
        options: [
          {
            id: "q4a1",
            text: "Congenital LQTS 2",
            level: "III",
            subQuestion: {
              id: "q4a",
              text: "LQTS control status:",
              options: [
                {
                  id: "q4a1",
                  text: "No previous events and taking beta-blocker",
                  level: "II-III",
                },
                {
                  id: "q4a2",
                  text: "Long QTC syndrome type 2",
                  level: "III",
                },
                {
                  id: "q4a3",
                  text: "Not controlled on therapy",
                  level: "III",
                },
              ],
            },
          },
          // {
          //   id: "q4a2",
          //   text: "Not congenital LQTS 2",
          //   subQuestion: {
          //     id: "q4b",
          //     text: "LQTS control status:",
          //     options: [
          //       {
          //         id: "q4b1",
          //         text: "No previous events and taking beta-blocker",
          //         level: "II-III",
          //       },
          //       {
          //         id: "q4b2",
          //         text: "Not adequately controlled on therapy",
          //         level: "III",
          //       },
          //     ],
          //   },
          // },
          {
            id: "q4a3",
            text: "Catecholaminergic polymorphic ventricular tachycardia",
            subQuestion: {
              id: "q4c",
              text: "CPVT control status:",
              options: [
                {
                  id: "q4c1",
                  text: "Well controlled with medical therapy",
                  level: "II-III",
                },
                {
                  id: "q4c2",
                  text: "Symptomatic, not well controlled with medical therapy",
                  level: "III",
                },
              ],
            },
          },
          {
            id: "q4a4",
            text: "Brugada Syndrome",
            subQuestion: {
              id: "q4d",
              text: "Brugada Syndrome history:",
              options: [
                {
                  id: "q4d1",
                  text: "No previous arrhythmias",
                  level: "II-III",
                },
                { id: "q4d2", text: "Previous arrhythmias", level: "III" },
              ],
            },
          },
        ],
      },
    ],
  },
  {
    id: "q5",
    text: "Do you have a heart muscle condition (cardiomyopathy)?",
    subQuestions: [
      {
        id: "q5a",
        text: "Type of cardiomyopathy:",
        options: [
          {
            id: "q5a1",
            text: "Hypertrophic cardiomyopathy",
            subQuestion: {
              id: "q5b",
              text: "Hypertrophic cardiomyopathy status:",
              options: [
                {
                  id: "q5b1",
                  text: "Gene test is positive with normal heart thickness",
                  level: "I",
                },
                {
                  id: "q5b2",
                  text: "Thick heart with normal function and no abnormal heart rhythms",
                  level: "II-III",
                },
                {
                  id: "q5b3",
                  text: "Thick heart with a gradient in your heart or abnormal heart rhythms",
                  level: "III",
                },
                {
                  id: "q5b4",
                  text: "Thick heart with chest pain, shortness of breath, lightheadedness or fainting due to high gradient in your heart",
                  level: "IV",
                },
                {
                  id: "q5b5",
                  text: "Thick heart with weakness of the heart pumping (LVEF< 50%)",
                  level: "IV",
                },
              ],
            },
          },
          {
            id: "q5a2",
            text: "Arrhythmogenic right ventricular cardiomyopathy",
            subQuestion: {
              id: "q5c",
              text: "ARVC status:",
              options: [
                {
                  id: "q5c1",
                  text: "Gene test positive with normal heart",
                  level: "II-III",
                },
                {
                  id: "q5c2",
                  text: "Gene test positive with mild changes on your heart",
                  level: "II-III",
                },
                {
                  id: "q5c3",
                  text: "Gene test positive with moderate or severe weakness of your heart",
                  level: "II-III",
                },
              ],
            },
          },
          {
            id: "q5a3",
            text: "Dilated cardiomyopathy",
            subQuestion: {
              id: "q5d",
              text: "Dilated cardiomyopathy status:",
              options: [
                {
                  id: "q5d1",
                  text: "Mildly reduced left ventricular systolic function (LVEF >45%)",
                  level: "II-III",
                },
                {
                  id: "q5d2",
                  text: "Moderately reduced left ventricular systolic function (LVEF 30-45%)",
                  level: "III",
                },
                {
                  id: "q5d3",
                  text: "Severely reduced left ventricular systolic function (LVEF <30%), or inability to climb one flight of stairs or symptoms at rest",
                  level: "IV",
                },
              ],
            },
          },
          {
            id: "q5a4",
            text: "Non-dilated left ventricular cardiomyopathy",
            subQuestion: {
              id: "q5e",
              text: "Non-dilated left ventricular cardiomyopathy status:",
              options: [
                {
                  id: "q5e1",
                  text: "Mildly reduced left ventricular systolic function (LVEF >45%)",
                  level: "II-III",
                },
                {
                  id: "q5e2",
                  text: "Moderately reduced left ventricular systolic function (LVEF 30-45%)",
                  level: "III",
                },
                {
                  id: "q5e3",
                  text: "Severely reduced left ventricular systolic function (LVEF <30%), or inability to climb one flight of stairs or symptoms at rest",
                  level: "IV",
                },
              ],
            },
          },
        ],
      },
    ],
  },
  {
    id: "q6",
    text: "Do you have congenital heart disease?",
    subQuestions: [
      {
        id: "q6a",
        text: "Type of congenital heart disease:",
        options: [
          {
            id: "q6a1",
            text: "Atrial septal defect",
            subQuestion: {
              id: "q6b",
              text: "Atrial septal defect status:",
              options: [
                {
                  id: "q6b1",
                  text: "Atrial septal defect after surgery or device closure",
                  level: "I",
                },
                {
                  id: "q6b2",
                  text: "Unrepaired without PH/arrhythmia",
                  level: "II",
                },
              ],
            },
          },
          {
            id: "q6a2",
            text: "Ventricular septal defect",
            subQuestion: {
              id: "q6c",
              text: "Ventricular septal defect status:",
              options: [
                {
                  id: "q6c1",
                  text: "Ventricular septal defect after surgery or device closure",
                  level: "I",
                },
                {
                  id: "q6c2",
                  text: "Unrepaired ventricular septal defect without high pressures in the lungs or abnormal heart rhythms",
                  level: "II",
                },
              ],
            },
          },
          {
            id: "q6a3",
            text: "Patent ductus arteriosus",
            subQuestion: {
              id: "q6d",
              text: "Patent ductus arteriosus status:",
              options: [
                {
                  id: "q6d1",
                  text: "After surgery or device closure",
                  level: "I",
                },
              ],
            },
          },
          {
            id: "q6a4",
            text: "Anomalous pulmonary venous drainage",
            subQuestion: {
              id: "q6e",
              text: "Anomalous pulmonary venous drainage status:",
              options: [{ id: "q6e1", text: "After surgery", level: "I" }],
            },
          },
          {
            id: "q6a5",
            text: "Repaired tetralogy of Fallot",
            subQuestion: {
              id: "q6f",
              text: "Repaired tetralogy of Fallot status:",
              options: [
                {
                  id: "q6f1",
                  text: "No significant tight valves, leaky valves or abnormal heart rhythms",
                  level: "I",
                },
              ],
            },
          },
          {
            id: "q6a6",
            text: "Repaired D-Transposition of the Great Arteries",
            subQuestion: {
              id: "q6g",
              text: "Repaired D-TGA status:",
              options: [
                {
                  id: "q6g1",
                  text: "Arterial switch surgery without tight or leaky valves",
                  level: "II",
                },
                {
                  id: "q6g2",
                  text: "Atrial switch with normal or mildly reduced heart pumping function",
                  level: "III",
                },
                {
                  id: "q6g3",
                  text: "Atrial switch with moderate or severely decreased heart pumping function",
                  level: "IV",
                },
              ],
            },
          },
          {
            id: "q6a7",
            text: "Congenitally corrected transposition of the Great Arteries",
            subQuestion: {
              id: "q6h",
              text: "Congenitally corrected TGA status:",
              options: [
                {
                  id: "q6h1",
                  text: "Normal or mildly reduced heart pumping function",
                  level: "III",
                },
                {
                  id: "q6h2",
                  text: "Moderate or severely decreased heart pumping function",
                  level: "IV",
                },
              ],
            },
          },
          {
            id: "q6a8",
            text: "Repaired atrioventricular septal defect",
            subQuestion: {
              id: "q6i",
              text: "Repaired AVSD status:",
              options: [
                {
                  id: "q6i1",
                  text: "No significant tight or leaky valves",
                  level: "III",
                },
              ],
            },
          },
          {
            id: "q6a9",
            text: "Aortic coarctation",
            subQuestion: {
              id: "q6j",
              text: "Aortic coarctation status:",
              options: [
                {
                  id: "q6j1",
                  text: "Repaired, without significant renarrowing",
                  level: "II-III",
                },
                {
                  id: "q6j2",
                  text: "Repaired coarctation with significant renarrowing",
                  level: "IV",
                },
                {
                  id: "q6j3",
                  text: "Unrepaired, severe coarctation",
                  level: "IV",
                },
              ],
            },
          },
          {
            id: "q6a10",
            text: "Ebstein anomaly",
            subQuestion: {
              id: "q6k",
              text: "Ebstein anomaly status:",
              options: [
                {
                  id: "q6k1",
                  text: "Mild to moderate leaky tricuspid valve, no tightness of tricuspid valve, no abnormal heart rhythms",
                  level: "II-III",
                },
                {
                  id: "q6k2",
                  text: "Any complication (severely leaky tricuspid valve, tight tricuspid valve, or abnormal heart rhythms)",
                  level: "III",
                },
              ],
            },
          },
          {
            id: "q6a11",
            text: "Fontan repair",
            subQuestion: {
              id: "q6l",
              text: "Fontan repair status:",
              options: [
                {
                  id: "q6l1",
                  text: "Good ventricular function, no significant leaky or tight valves, no arrhythmias, with good exercise capacity and normal oxygen levels",
                  level: "III",
                },
                {
                  id: "q6l2",
                  text: "Any complication – low heart pumping function, leaky valves, tight valves, arrhythmias, low oxygen levels",
                  level: "IV",
                },
              ],
            },
          },
        ],
      },
    ],
  },
  {
    id: "q7",
    text: "Do you have a tight or leaky valve?",
    subQuestions: [
      {
        id: "q7a",
        text: "Which valve?",
        options: [
          {
            id: "q7a1",
            text: "Aortic",
            subQuestion: {
              id: "q7b",
              text: "Aortic valve issue:",
              options: [
                {
                  id: "q7b1",
                  text: "Tight aortic valve",
                  subQuestion: {
                    id: "q7c",
                    text: "Tight aortic valve severity:",
                    options: [
                      { id: "q7c1", text: "Mild", level: "II-III" },
                      { id: "q7c2", text: "Moderate", level: "II-III" },
                      { id: "q7c3", text: "Severe", level: "III" },
                    ],
                  },
                },
                {
                  id: "q7b2",
                  text: "Leaky aortic valve",
                  subQuestion: {
                    id: "q7d",
                    text: "Leaky aortic valve severity:",
                    options: [
                      { id: "q7d1", text: "Mild", level: "II-III" },
                      { id: "q7d2", text: "Moderate", level: "II-III" },
                      { id: "q7d3", text: "Severe", level: "III" },
                    ],
                  },
                },
              ],
            },
          },
          {
            id: "q7a2",
            text: "Mitral",
            subQuestion: {
              id: "q7e",
              text: "Mitral valve issue:",
              options: [
                {
                  id: "q7e1",
                  text: "Tight mitral valve",
                  subQuestion: {
                    id: "q7f",
                    text: "Tight mitral valve severity:",
                    options: [
                      { id: "q7f1", text: "Mild", level: "II-III" },
                      { id: "q7f2", text: "Moderate", level: "III" },
                      { id: "q7f3", text: "Severe", level: "IV" },
                    ],
                  },
                },
                {
                  id: "q7e2",
                  text: "Leaky mitral valve",
                  subQuestion: {
                    id: "q7g",
                    text: "Leaky mitral valve severity:",
                    options: [
                      { id: "q7g1", text: "Mild", level: "II-III" },
                      { id: "q7g2", text: "Moderate", level: "II-III" },
                      { id: "q7g3", text: "Severe", level: "III" },
                    ],
                  },
                },
              ],
            },
          },
          {
            id: "q7a3",
            text: "Pulmonary",
            subQuestion: {
              id: "q7h",
              text: "Pulmonary valve issue:",
              options: [
                {
                  id: "q7h1",
                  text: "Tight pulmonary valve",
                  subQuestion: {
                    id: "q7i",
                    text: "Tight pulmonary valve severity:",
                    options: [
                      { id: "q7i1", text: "Mild", level: "I" },
                      { id: "q7i2", text: "Moderate", level: "II-III" },
                      { id: "q7i3", text: "Severe", level: "II-III" },
                    ],
                  },
                },
                {
                  id: "q7h2",
                  text: "Leaky pulmonary valve",
                  subQuestion: {
                    id: "q7j",
                    text: "Leaky pulmonary valve severity:",
                    options: [
                      { id: "q7j1", text: "Mild", level: "II-III" },
                      { id: "q7j2", text: "Moderate", level: "II-III" },
                      { id: "q7j3", text: "Severe", level: "II-III" },
                    ],
                  },
                },
              ],
            },
          },
          {
            id: "q7a4",
            text: "Tricuspid",
            subQuestion: {
              id: "q7k",
              text: "Tricuspid valve issue:",
              options: [
                {
                  id: "q7k1",
                  text: "Tight tricuspid valve",
                  subQuestion: {
                    id: "q7l",
                    text: "Tight tricuspid valve severity:",
                    options: [
                      { id: "q7l1", text: "Mild", level: "II-III" },
                      { id: "q7l2", text: "Moderate", level: "II-III" },
                      { id: "q7l3", text: "Severe", level: "II-III" },
                    ],
                  },
                },
                {
                  id: "q7k2",
                  text: "Leaky tricuspid valve",
                  subQuestion: {
                    id: "q7m",
                    text: "Leaky tricuspid valve severity:",
                    options: [
                      { id: "q7m1", text: "Mild", level: "II-III" },
                      { id: "q7m2", text: "Moderate", level: "II-III" },
                      { id: "q7m3", text: "Severe", level: "II-III" },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    ],
  },
  {
    id: "q8",
    text: "Have you had a valve replaced?",
    subQuestions: [
      {
        id: "q8a",
        text: "Valve replacement type:",
        options: [
          {
            id: "q8a1",
            text: "Mechanical valve",
            level: "III",
          },
          {
            id: "q8a2",
            text: "Tissue valve or homograft",
            subQuestion: {
              id: "q8c",
              text: "Which valve was replaced?",
              options: [
                { id: "q8c1", text: "Pulmonary valve", level: "II-III" },
                { id: "q8c2", text: "Tricuspid valve", level: "II-III" },
                {
                  id: "q8c3",
                  text: "Mitral valve",
                  subQuestion: {
                    id: "q8d",
                    text: "Mitral tissue valve status:",
                    options: [
                      { id: "q8d1", text: "Mild tightness", level: "II-III" },
                      { id: "q8d2", text: "Moderate tightness", level: "III" },
                      { id: "q8d3", text: "Severely tight", level: "IV" },
                      { id: "q8d4", text: "Leaks mildly", level: "II-III" },
                      { id: "q8d5", text: "Leaks moderately", level: "II-III" },
                      { id: "q8d6", text: "Leaks severely", level: "III" },
                    ],
                  },
                },
                {
                  id: "q8c4",
                  text: "Aortic valve",
                  subQuestion: {
                    id: "q8e",
                    text: "Aortic tissue valve status:",
                    options: [
                      { id: "q8e1", text: "Mild tightness", level: "I" },
                      {
                        id: "q8e2",
                        text: "Moderate tightness",
                        level: "II-III",
                      },
                      { id: "q8e3", text: "Severely tight", level: "III" },
                      { id: "q8e4", text: "Leaks mildly", level: "II-III" },
                      { id: "q8e5", text: "Leaks moderately", level: "II-III" },
                      { id: "q8e6", text: "Leaks severely", level: "II-III" },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    ],
  },
  // {
  //   id: "q8",
  //   text: "Have you had a valve replaced?",
  //   subQuestions: [
  //     {
  //       id: "q8a",
  //       text: "Valve replacement type:",
  //       options: [
  //         { id: "q8a1", text: "Mechanical valve (metal)", level: "III" },
  //       ],
  //     },
  //   ],
  // },
  {
    id: "q9",
    text: "Have you had your valve replaced with a tissue valve or homograft?",
    subQuestions: [
      {
        id: "q9a",
        text: "Which valve was replaced?",
        options: [
          { id: "q9a1", text: "Pulmonary valve", level: "II-III" },
          { id: "q9a2", text: "Tricuspid valve", level: "II-III" },
          {
            id: "q9a3",
            text: "Mitral valve",
            subQuestion: {
              id: "q9b",
              text: "Mitral tissue valve status:",
              options: [
                { id: "q9b1", text: "Mild tightness", level: "II-III" },
                { id: "q9b2", text: "Moderate tightness", level: "III" },
                { id: "q9b3", text: "Severely tight", level: "IV" },
                { id: "q9b4", text: "Leaks mildly", level: "II-III" },
                { id: "q9b5", text: "Leaks moderately", level: "II-III" },
                { id: "q9b6", text: "Leaks severely", level: "III" },
              ],
            },
          },
          {
            id: "q9a4",
            text: "Aortic valve",
            subQuestion: {
              id: "q9c",
              text: "Aortic tissue valve status:",
              options: [
                { id: "q9c1", text: "Mild tightness", level: "I" },
                { id: "q9c2", text: "Moderate tightness", level: "II-III" },
                { id: "q9c3", text: "Severely tight", level: "III" },
                { id: "q9c4", text: "Leaks mildly", level: "II-III" },
                { id: "q9c5", text: "Leaks moderately", level: "II-III" },
                { id: "q9c6", text: "Leaks severely", level: "II-III" },
              ],
            },
          },
        ],
      },
    ],
  },
  {
    id: "q10",
    text: "Do you have an abnormal aorta (aortopathy)?",
    subQuestions: [
      {
        id: "q10a",
        text: "Type of aortopathy:",
        options: [
          {
            id: "q10a1",
            text: "Turner syndrome",
            subQuestion: {
              id: "q10b",
              text: "Turner syndrome status:",
              options: [
                {
                  id: "q10b1",
                  text: "No heart issues (normal aortic valve, no coarctation, normal aortic size)",
                  level: "II",
                },
                {
                  id: "q10b2",
                  text: "Indexed aortic dimension 20-25 mm/m2",
                  level: "III",
                },
                {
                  id: "q10b3",
                  text: "Indexed aortic dimension >25 mm/m2",
                  level: "IV",
                },
              ],
            },
          },
          {
            id: "q10a2",
            text: "Marfan syndrome",
            subQuestion: {
              id: "q10c",
              text: "Marfan syndrome status:",
              options: [
                {
                  id: "q10c1",
                  text: "Normal aortic dimensions",
                  level: "II-III",
                },
                {
                  id: "q10c2",
                  text: "Aortic dimension 40-45 mm",
                  level: "III",
                },
                { id: "q10c3", text: "Aortic dimension >45 mm", level: "IV" },
                {
                  id: "q10c4",
                  text: "Prior aortic root replacement",
                  level: "III",
                },
              ],
            },
          },
          {
            id: "q10a3",
            text: "Loeys-Dietz",
            subQuestion: {
              id: "q10d",
              text: "Loeys-Dietz status:",
              options: [
                {
                  id: "q10d1",
                  text: "Normal aortic dimensions",
                  level: "II-III",
                },
                {
                  id: "q10d2",
                  text: "Aortic dimension 40-45 mm",
                  level: "III",
                },
                { id: "q10d3", text: "Aortic dimension >45 mm", level: "IV" },
              ],
            },
          },
          {
            id: "q10a4",
            text: "Bicuspid aortic valve with enlarged aorta",
            subQuestion: {
              id: "q10e",
              text: "Bicuspid aortic valve status:",
              options: [
                { id: "q10e1", text: "Aorta <45 mm", level: "II-III" },
                {
                  id: "q10e2",
                  text: "Aortic dimension 45-50 mm",
                  level: "III",
                },
                { id: "q10e3", text: "Aortic dimension >50 mm", level: "IV" },
              ],
            },
          },
          {
            id: "q10a5",
            text: "Vascular Ehlers Danlos",
            subQuestion: {
              id: "q10f",
              text: "Vascular Ehlers Danlos status:",
              options: [
                { id: "q10f1", text: "Any aortic dimension", level: "IV" },
              ],
            },
          },
          {
            id: "q10a6",
            text: "Aortic dissection",
            subQuestion: {
              id: "q10g",
              text: "Aortic dissection status:",
              options: [
                {
                  id: "q10g1",
                  text: "Previous aortic dissection with stable aortic size",
                  level: "III",
                },
                {
                  id: "q10g2",
                  text: "Previous aortic dissection with increasing aortic size",
                  level: "IV",
                },
              ],
            },
          },
        ],
      },
    ],
  },
  {
    id: "q11",
    text: "Do you have an issue with the arteries of your heart (coronary arteries)?",
    subQuestions: [
      {
        id: "q11a",
        text: "Coronary artery issue:",
        options: [
          {
            id: "q11a1",
            text: "Spontaneous coronary artery dissection",
            level: "III",
          },
          { id: "q11a2", text: "Prior heart attack", level: "III" },
        ],
      },
    ],
  },
];

// Component to render a radio group for sub-questions
function SubQuestionGroup({ subQuestion, answers, onAnswer }) {
  const selectedOption = answers[subQuestion.id];
  const selectedOptionData = subQuestion.options.find(
    (opt) => opt.id === selectedOption,
  );

  return (
    <div className="mwho-subquestion">
      <p className="mwho-subquestion-text">{subQuestion.text}</p>
      <div className="mwho-options">
        {subQuestion.options.map((option) => (
          <label key={option.id} className="mwho-option">
            <input
              type="radio"
              name={subQuestion.id}
              value={option.id}
              checked={selectedOption === option.id}
              onChange={() => onAnswer(subQuestion.id, option.id)}
            />
            <span className="mwho-option-text">{option.text}</span>
            {option.level && (
              <span className="mwho-level-badge" data-level={option.level}>
                mWHO {option.level}
              </span>
            )}
          </label>
        ))}
      </div>

      {/* Render nested sub-question if the selected option has one */}
      {selectedOptionData?.subQuestion && (
        <SubQuestionGroup
          subQuestion={selectedOptionData.subQuestion}
          answers={answers}
          onAnswer={onAnswer}
        />
      )}
    </div>
  );
}

// Check if all required questions for current slide are answered
function isSlideComplete(question, answers) {
  // Must have answered the top-level question
  if (!answers[question.id]) {
    return false;
  }

  // If answered "No", slide is complete
  if (answers[question.id] === "no") {
    return true;
  }

  // If answered "Yes", must complete all sub-question chains
  const checkSubQuestionChain = (subQuestions) => {
    for (const subQ of subQuestions) {
      if (!answers[subQ.id]) return false;

      const selectedOption = subQ.options.find(
        (opt) => opt.id === answers[subQ.id],
      );
      if (selectedOption?.subQuestion) {
        if (!checkSubQuestionChain([selectedOption.subQuestion])) return false;
      }
    }
    return true;
  };

  if (question.subQuestions) {
    return checkSubQuestionChain(question.subQuestions);
  }

  return true;
}

// Calculate the highest mWHO level from all answers
function calculateHighestLevel(answers) {
  let highestIndex = -1;

  const checkSubQuestion = (subQ, answersMap) => {
    const selectedId = answersMap[subQ.id];
    if (selectedId) {
      const selectedOption = subQ.options.find((opt) => opt.id === selectedId);
      if (selectedOption) {
        if (selectedOption.level) {
          const idx = getLevelIndex(selectedOption.level);
          if (idx > highestIndex) {
            highestIndex = idx;
          }
        }
        if (selectedOption.subQuestion) {
          checkSubQuestion(selectedOption.subQuestion, answersMap);
        }
      }
    }
  };

  for (const question of questions) {
    if (answers[question.id] === "yes" && question.subQuestions) {
      for (const subQ of question.subQuestions) {
        checkSubQuestion(subQ, answers);
      }
    }
  }

  return highestIndex >= 0 ? WHO_LEVELS[highestIndex] : null;
}

export default function MWHO() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [urlDisplayLevel, setUrlDisplayLevel] = useState(null);

  // console.log('mWHO Survey State:', { currentSlide, answers, showResults, urlDisplayLevel })

  // Check for URL parameters on mount
  useEffect(() => {
    const { results } = getUrlParams();
    if (results) {
      const level = urlParamToLevel(results);
      if (level) {
        setUrlDisplayLevel(level);
        setShowResults(true);
      }
    }
  }, []);

  const currentQuestion = questions[currentSlide];
  const totalSlides = questions.length;

  const handleTopLevelAnswer = (answer) => {
    const newAnswers = { ...answers, [currentQuestion.id]: answer };

    // Clear sub-question answers if changing to "No"
    if (answer === "no") {
      const clearSubAnswers = (subQuestions) => {
        for (const subQ of subQuestions) {
          delete newAnswers[subQ.id];
          for (const opt of subQ.options) {
            if (opt.subQuestion) {
              clearSubAnswers([opt.subQuestion]);
            }
          }
        }
      };
      if (currentQuestion.subQuestions) {
        clearSubAnswers(currentQuestion.subQuestions);
      }
    }

    setAnswers(newAnswers);
  };

  const handleSubAnswer = (questionId, optionId) => {
    const newAnswers = { ...answers, [questionId]: optionId };

    // Clear any nested sub-question answers when selection changes
    const clearNestedAnswers = (subQuestions, startClearing = false) => {
      for (const subQ of subQuestions) {
        if (startClearing) {
          delete newAnswers[subQ.id];
        }
        if (subQ.id === questionId) {
          // Found the changed question, clear all nested from here
          for (const opt of subQ.options) {
            if (opt.subQuestion) {
              clearNestedAnswers([opt.subQuestion], true);
            }
          }
        } else {
          for (const opt of subQ.options) {
            if (opt.subQuestion) {
              clearNestedAnswers([opt.subQuestion], startClearing);
            }
          }
        }
      }
    };

    if (currentQuestion.subQuestions) {
      clearNestedAnswers(currentQuestion.subQuestions);
    }

    setAnswers(newAnswers);
  };

  const canProceed = useMemo(() => {
    return isSlideComplete(currentQuestion, answers);
  }, [currentQuestion, answers]);

  const handleNext = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      // Calculate result and reload with query parameter
      const result = calculateHighestLevel(answers);
      const urlParam = levelToUrlParam(result);

      if (urlParam) {
        // Reload page with results parameter
        const url = new URL(window.location);
        url.searchParams.set("results", urlParam);
        window.location.href = url.toString();
      } else {
        // No specific classification, show results normally
        setShowResults(true);
      }
    }
  };

  const handlePrev = () => {
    if (showResults) {
      setShowResults(false);
    } else if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleRestart = () => {
    // Clear URL parameters and trigger a full page refresh
    const url = new URL(window.location);
    url.searchParams.delete("results");
    window.location.href = url.toString();
  };

  const highestLevel = useMemo(() => calculateHighestLevel(answers), [answers]);

  // Use URL display level if available, otherwise use calculated level
  const displayLevel = urlDisplayLevel || highestLevel;

  // Get description from WordPress data
  const getResultDescription = (level) => {
    // console.log('Getting description for level:', level)
    // console.log(ccbSurveyData)
    if (!level || !ccbSurveyData?.resultsData) return null;

    const fieldKey = `mwho_${level.toLowerCase().replace("-", "-")}_description`;
    // console.log('Looking for field key:', fieldKey)
    // console.log('Available fields:', Object.keys(ccbSurveyData.resultsData))

    return ccbSurveyData.resultsData[fieldKey];
  };

  // Results
  if (showResults) {
    return (
      <div className="mwho-survey">
        <div className="mwho-results">
          {displayLevel ? (
            <>
              <h4>Your Classification:</h4>
              <div className="mwho-result-level" data-level={displayLevel}>
                mWHO {displayLevel}
              </div>
              <div className="mwho-result-description">
                {getResultDescription(displayLevel) && (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: getResultDescription(displayLevel),
                    }}
                  />
                )}
              </div>
              <div className="mwho-result-disclaimer">
                {ccbSurveyData?.resultsDisclaimer}
              </div>
            </>
          ) : (
            <div className="mwho-result-none">
              <h4>No Score</h4>
              <p>
                {ccbSurveyData?.resultsData?.no_score ||
                  "No specific mWHO risk classification was identified. Please consult with your healthcare provider for personalized guidance."}
              </p>
            </div>
          )}
          <div className="mwho-nav">
            <button
              className="btn arrow-link red"
              type="button"
              onClick={handleRestart}
            >
              <span className="title" data-title="Start Over">
                <span className="title-text">Start Over</span>
                <span className="title-bold-spacer">Start Over</span>
              </span>
              <svg
                className="link-arrow white-link"
                width="60.9075413"
                height="23"
                viewBox="0 0 60.9075413 23"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  className="circle"
                  cx="35"
                  cy="11"
                  r="10.5"
                  stroke="#EA0029"
                />
                <line
                  className="line"
                  x1="0"
                  y1="10.9453"
                  x2="39"
                  y2="10.9453"
                  stroke="#EA0029"
                />
                <path
                  className="arrow"
                  transform-origin="100% 50%"
                  stroke="#EA0029"
                  d="M34,16.9452991l5-5.999999-5-5.99999"
                />
                <path
                  className="heart"
                  opacity="0"
                  stroke="#EA0029"
                  d="M58.7248557,11.1299175c.8732012-1.0419667,1.3983597-2.3794889,1.3983597-3.83914.0007368-3.322673-2.7200319-6.0156097-6.075484-6.0156097-2.0735386,0-3.904295,1.028834-5.0006808,2.5990389-1.0971225-1.5702049-2.9279353-2.5990389-5.0014175-2.5990389-3.3561325,0-6.0762207,2.6929366-6.0762207,6.0156097,0,1.4596511.5252148,2.7971733,1.398416,3.83914.1094622.1304803.2245475.2568141.3445193.3776949l9.3339662,10.2172197,9.3339662-10.2172197c.1199696-.1208808.235055-.2465321.3445757-.3776949Z"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Survey
  return (
    <div className="mwho-survey">
      <div className="mwho-slide">
        <div className="question-number">
          Question {currentSlide + 1} of {totalSlides}
        </div>
        <h3 className="mwho-question-text">{currentQuestion.text}</h3>

        <div className="mwho-top-level-options">
          <label className="mwho-option mwho-option-yes">
            <input
              type="radio"
              name={currentQuestion.id}
              value="yes"
              checked={answers[currentQuestion.id] === "yes"}
              onChange={() => handleTopLevelAnswer("yes")}
            />
            <span className="mwho-option-text">Yes</span>
          </label>
          <label className="mwho-option mwho-option-no">
            <input
              type="radio"
              name={currentQuestion.id}
              value="no"
              checked={answers[currentQuestion.id] === "no"}
              onChange={() => handleTopLevelAnswer("no")}
            />
            <span className="mwho-option-text">No</span>
          </label>
        </div>

        {/* Show sub-questions if answered "Yes" */}
        {answers[currentQuestion.id] === "yes" &&
          currentQuestion.subQuestions?.map((subQ) => (
            <SubQuestionGroup
              key={subQ.id}
              subQuestion={subQ}
              answers={answers}
              onAnswer={handleSubAnswer}
            />
          ))}
      </div>

      <div className="mwho-nav">
        <div className="mwho-progress-bar">
          <div
            className="mwho-progress-fill"
            style={{ width: `${((currentSlide + 1) / totalSlides) * 100}%` }}
          />
        </div>

        <button
          type="button"
          onClick={handlePrev}
          disabled={currentSlide === 0}
          className="mwho-btn mwho-btn-secondary"
        >
          Previous
        </button>
        <button
          type="button"
          onClick={handleNext}
          disabled={!canProceed}
          className="mwho-btn mwho-btn-primary"
        >
          {currentSlide === totalSlides - 1 ? "View Results" : "Next"}
        </button>
      </div>
    </div>
  );
}
